import { useState } from 'react';
import { z } from 'zod';
import { MissingResourceError } from '#api/errors.ts';
import { useSuspenseRouteQuery } from '#api/routes.queries.ts';
import { DeleteIcon } from '#assets/ReactSvg.ts';
import { Button } from '#components/atoms/buttons/Button.tsx';
import { Link } from '#components/atoms/link/Link.tsx';
import { CopyPill } from '#components/atoms/pill/copy-pill/CopyPill.tsx';
import { Tooltip } from '#components/atoms/tooltip/Tooltip.tsx';
import { MainLayout } from '#components/patterns/layouts/main/MainLayout.tsx';
import { PageHeader } from '#components/patterns/page-header/PageHeader.tsx';
import { useReturnNavigation } from '#hooks/useAppLocation.ts';
import { useZodParams } from '#hooks/useZodParams.ts';
import { SKIP_UNSAVED_CHANGES_PROMPT } from '#screens/roles/detail/form/BlockNavigationDialog.tsx';
import { DeleteRouteDialog } from '#screens/routes/delete/DeleteRouteDialog.tsx';
import { fromArray, Titled } from '#util/title.ts';
import { RouteForm } from '../form/RouteForm.tsx';
import { mapRouteToForm } from './mapRouteToForm.ts';

export function EditRoutePage() {
  const { routeId } = useZodParams({
    routeId: z.string().refine((id): id is `route-${string}` => id.startsWith('route-')),
  });
  const {
    data: { data: route },
    error,
    isFetching,
  } = useSuspenseRouteQuery(routeId);
  const [showDeletionModal, setShowDeletionModal] = useState<boolean>(false);
  const returnTo = useReturnNavigation('/routes');

  // Check if this has been deleted (e.g. in a different tab)
  if (error && !isFetching && error instanceof MissingResourceError) {
    throw error;
  }

  return (
    <Titled title={(title) => fromArray([route.name, title])}>
      <MainLayout
        header={
          <PageHeader
            title={
              <>
                <Link to="/routes" className="a">
                  Routes
                </Link>
                {' / '}
                {route.name}
              </>
            }
            subtitle={
              <>
                ID: <CopyPill value={route.id} />
              </>
            }
          >
            <Tooltip
              content="Delete"
              render={
                <Button
                  size="small"
                  variant="icon"
                  onClick={() => {
                    setShowDeletionModal(true);
                  }}
                />
              }
            >
              <DeleteIcon aria-hidden="true" />
              <span className="visually-hidden">Delete</span>
            </Tooltip>
          </PageHeader>
        }
      >
        <RouteForm
          routeId={routeId}
          defaultValues={mapRouteToForm(route)}
          onSuccess={() => {
            returnTo();
          }}
        />
        {showDeletionModal ?
          <DeleteRouteDialog
            routeId={routeId}
            onCancel={() => setShowDeletionModal(false)}
            onDelete={() => {
              returnTo({ state: SKIP_UNSAVED_CHANGES_PROMPT });
            }}
          />
        : null}
      </MainLayout>
    </Titled>
  );
}
